import React, { useContext } from 'react';
import { validateDate } from 'src/util/util';
import { toLocalDate } from 'src/util/partialDate';
import { LocalizationProvider } from '@mui/x-date-pickers';
// date-fns@2.x is pinned in this project; use the V2 adapter path (the
// default AdapterDateFns in @mui/x-date-pickers v8 expects date-fns v3+).
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Box, FormHelperText } from '@mui/material';
import { ThemeContext } from 'src/store/ContextStore';

const defaultInvalidDateMessage = 'Invalid date.';

export default function DateInput(props) {
  const {
    label,
    value,
    onChange,
    disableFuture,
    fullWidth,
    disabled,
    variant,
    showPlaceholder,
    minDateMessage,
    maxDateMessage,
    minDate,
    allowDateSelection = true,
    error,
    helperText,
    liveText = false,
    helperTextColour,
    slotProps = {}
  } = props;
  const effectiveFullWidth = fullWidth ?? true;

  const { theme } = useContext(ThemeContext);
  let format = props.format ?? theme.dateFormat;

  // Translate the stored moment-style tokens to date-fns ones for the picker.
  // The dot format used to map to SLASHES, so an admin who chose DD.MM.YYYY got
  // dd/MM/yyyy everywhere — the separator was silently discarded. Display
  // surfaces avoid this table entirely by formatting with moment, which takes
  // the stored tokens verbatim (see src/hooks/useDateFormatter.js).
  if (format == 'MM/DD/YYYY') format = 'MM/dd/yyyy';
  if (format == 'DD.MM.YYYY') format = 'dd.MM.yyyy';
  if (format == 'DD/MM/YYYY') format = 'dd/MM/yyyy';

  // These were computed and then never passed to the DatePicker, so
  // `allowDateSelection` was inert everywhere it was used and a group that had
  // configured year-only capture still got a full day-and-month calendar
  // (TGCLOUD-1632).
  //
  // The view is named 'day' in @mui/x-date-pickers v9 — 'date' was the v5
  // spelling and is rejected by the propType. ['year','day'] is also v9's own
  // default (DatePicker/shared.js defaultViews), so the allowDateSelection
  // branch below is a no-op for every consumer that does not opt out.
  //
  // openTo must be passed too: it defaults to 'day', and when 'day' is absent
  // from `views` MUI console.warns and falls back to views[0].
  const views = allowDateSelection ? ['year', 'day'] : ['year'];
  const openTo = allowDateSelection ? 'day' : 'year';

  const [invalidDateMessage, setInvalidDateMessage] = React.useState('');
  const [date, setDate] = React.useState(value);

  // React.useEffect(() => {
  //   console.log(value);
  //   setDate(null);
  // }, [error, helperText, value]);

  // used to disable the invalid date message when typing
  const onFocus = React.useCallback(() => {
    setInvalidDateMessage('');
  }, []);

  // check if valid date, then set the value otherwise show the error
  const onBlur = (e) => {
    if (date === null || validateDate(date)) onChange(date);
    else setInvalidDateMessage(defaultInvalidDateMessage);
  };

  React.useEffect(() => {
    if (liveText) {
      if (error && helperText) setInvalidDateMessage(helperText);
      else setInvalidDateMessage('');
    }
  }, [value]);

  const onDateChange = (date) => {
    if (error && helperText) setInvalidDateMessage(helperText);
    onChange(date);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        readOnly={props.readOnly}
        disabled={disabled}
        // toLocalDate, not new Date(): a bare 'YYYY-MM-DD' parses as UTC
        // midnight, which renders the previous day at negative UTC offsets.
        value={toLocalDate(value)}
        onChange={onDateChange}
        disableFuture={disableFuture}
        format={format}
        views={views}
        openTo={openTo}
        // onAccept={onAccept}
        minDate={toLocalDate(minDate) || new Date(1600, 0, 1)}
        slotProps={{
          textField: {
            error: !!error,
            helperText: error ? helperText : '',
            fullWidth: effectiveFullWidth,
            ...slotProps.textField
          }
        }}
      />
    </LocalizationProvider>
  );
}
